import { cn } from "@/lib/utils";
import { ChangeEvent, useRef } from "react";
import { Button } from "./button";
import { useToast } from "./use-toast";

export interface FileUploaderProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSize?: number; // in bytes
  className?: string;
  buttonText?: string;
  variant?: "default" | "outline" | "ghost";
  showPreview?: boolean;
}

export function FileUploader({
  onFileSelect,
  accept = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB default
  className,
  buttonText = "Select File",
  variant = "default",
}: FileUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const formatBytes = (bytes: number) => {
    return (bytes / (1024 * 1024)).toFixed(2) + "MB";
  };

  const validateFile = (file: File): boolean => {
    if (accept) {
      const acceptedTypes = accept.split(",").map((type) => type.trim());
      const isValidType = acceptedTypes.some((acceptedType) => {
        if (acceptedType.endsWith("/*")) {
          // Handle wildcard MIME types (e.g., image/*)
          const typePrefix = acceptedType.slice(0, -1); // Remove the '*'
          return file.type.startsWith(typePrefix);
        }
        // Handle specific MIME types
        return file.type === acceptedType;
      });

      if (!isValidType) {
        toast({
          title: "Invalid file type",
          description: `Please select a file of type: ${accept}`,
          variant: "destructive",
        });
        return false;
      }
    }

    if (maxSize && file.size > maxSize) {
      toast({
        title: "File too large",
        description: `Please select a file smaller than ${formatBytes(maxSize)}`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (validateFile(file)) {
      onFileSelect(file);
    }

    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={accept}
        className="hidden"
      />
      <Button type="button" onClick={handleClick} variant={variant}>
        {buttonText}
      </Button>
    </div>
  );
}
